import { useState, useEffect, useRef, useCallback } from 'react';
import { X, Send, Mic, MicOff, Trash2, Sparkles, Volume2, VolumeX } from 'lucide-react';
import api from '@/utils/api';
import { toast } from 'sonner';

const BRAND = { navy: '#0B1120', gold: '#C9A84C' };
const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AssistantChat({ user }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [listening, setListening] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const [voiceEnabled, setVoiceEnabled] = useState(true);
  const [autoListen, setAutoListen] = useState(true);
  const [sessionId] = useState(() => `assistant-${user?.id || 'anon'}`);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const recognitionRef = useRef(null);
  const chatBodyRef = useRef(null);
  const audioRef = useRef(null);
  const autoListenRef = useRef(true);
  const voiceEnabledRef = useRef(true);

  // Keep refs in sync with state
  useEffect(() => { autoListenRef.current = autoListen; }, [autoListen]);
  useEffect(() => { voiceEnabledRef.current = voiceEnabled; }, [voiceEnabled]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => { scrollToBottom(); }, [messages]);

  // Load history on open
  useEffect(() => {
    if (!open) return;
    const loadHistory = async () => {
      try {
        const res = await api.get(`/assistant/history/${sessionId}`);
        setMessages((res.data || []).map(m => ({ role: m.role, content: m.content, id: m.id })));
      } catch {}
    };
    loadHistory();
    setTimeout(() => inputRef.current?.focus(), 200);
  }, [open, sessionId]);

  // Voice recognition setup
  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) return;
    const recognition = new SpeechRecognition();
    recognition.continuous = false;
    recognition.interimResults = true;
    recognition.lang = 'hi-IN';
    recognition.onresult = (event) => {
      let transcript = '';
      for (let i = 0; i < event.results.length; i++) {
        transcript += event.results[i][0].transcript;
      }
      setInput(transcript);
      if (event.results[event.results.length - 1].isFinal) {
        setListening(false);
      }
    };
    recognition.onerror = () => setListening(false);
    recognition.onend = () => setListening(false);
    recognitionRef.current = recognition;
  }, []);

  const toggleVoice = () => {
    if (!recognitionRef.current) {
      toast.error('Voice not supported in this browser');
      return;
    }
    if (listening) {
      recognitionRef.current.stop();
      setListening(false);
    } else {
      setInput('');
      recognitionRef.current.start();
      setListening(true);
    }
  };

  const startListeningAuto = useCallback(() => {
    if (!recognitionRef.current || !autoListenRef.current) return;
    try {
      setInput('');
      recognitionRef.current.start();
      setListening(true);
    } catch (e) {
      console.log('Auto-listen start failed:', e);
    }
  }, []);

  const stopAudio = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current = null;
    }
    setSpeaking(false);
  }, []);

  const playVoice = useCallback(async (text, triggerAutoListen = false) => {
    if (!voiceEnabledRef.current || !text) return;
    stopAudio();
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/assistant/tts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ text })
      });
      if (!res.ok) throw new Error('TTS failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);
      audioRef.current = audio;
      setSpeaking(true);
      audio.onended = () => {
        setSpeaking(false);
        URL.revokeObjectURL(url);
        audioRef.current = null;
        if (triggerAutoListen && autoListenRef.current) {
          setTimeout(() => startListeningAuto(), 400);
        }
      };
      audio.onerror = () => { setSpeaking(false); URL.revokeObjectURL(url); audioRef.current = null; };
      await audio.play();
    } catch (err) {
      console.error('Voice playback failed:', err);
      setSpeaking(false);
    }
  }, [stopAudio, startListeningAuto]);

  const sendMessage = useCallback(async (text) => {
    const msg = (text || input).trim();
    if (!msg || loading) return;
    setInput('');
    stopAudio();
    const userMsg = { role: 'user', content: msg, id: Date.now() };
    setMessages(prev => [...prev, userMsg]);
    setLoading(true);
    try {
      const res = await api.post('/assistant/chat', { message: msg, session_id: sessionId });
      const asstMsg = { role: 'assistant', content: res.data.response, id: res.data.message_id };
      setMessages(prev => [...prev, asstMsg]);
      playVoice(res.data.response, true);
    } catch (err) {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Sorry, abhi thoda issue aa raha hai. Please try again!', id: 'err-' + Date.now() }]);
    } finally {
      setLoading(false);
    }
  }, [input, loading, sessionId, playVoice, stopAudio]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  const clearChat = async () => {
    try {
      await api.delete(`/assistant/history/${sessionId}`);
      setMessages([]);
      toast.success('Chat cleared!');
    } catch {}
  };

  // Auto-send after voice stops
  useEffect(() => {
    if (!listening && input.trim() && recognitionRef.current) {
      const timer = setTimeout(() => { if (input.trim()) sendMessage(input); }, 500);
      return () => clearTimeout(timer);
    }
  }, [listening, input, sendMessage]);

  if (!user) return null;

  return (
    <>
      {/* Floating Button */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-20 md:bottom-6 right-4 md:right-6 z-50 w-14 h-14 rounded-full shadow-2xl flex items-center justify-center transition-all duration-300 hover:scale-110 active:scale-95"
          style={{ background: `linear-gradient(135deg, ${BRAND.navy}, #1a2840)`, boxShadow: `0 4px 20px ${BRAND.navy}60` }}
          data-testid="assistant-open-btn"
        >
          <Sparkles size={24} style={{ color: BRAND.gold }} />
          {messages.length === 0 && (
            <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-emerald-500 border-2 border-white animate-pulse"></span>
          )}
        </button>
      )}

      {/* Chat Window */}
      {open && (
        <div className="fixed bottom-0 md:bottom-6 right-0 md:right-6 z-50 w-full md:w-96 h-full md:h-[520px] md:rounded-2xl overflow-hidden shadow-2xl flex flex-col" style={{ background: '#fff', border: `1px solid ${BRAND.navy}20` }} data-testid="assistant-chat-window">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 shrink-0" style={{ background: `linear-gradient(135deg, ${BRAND.navy}, #1a2840)` }}>
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full flex items-center justify-center relative" style={{ background: `${BRAND.gold}25` }}>
                <Sparkles size={18} style={{ color: BRAND.gold }} />
                {speaking && <span className="absolute inset-0 rounded-full border-2 animate-ping" style={{ borderColor: BRAND.gold }}></span>}
              </div>
              <div>
                <h3 className="text-white font-bold text-sm">Ganesa AI</h3>
                <p className="text-xs" style={{ color: `${BRAND.gold}90` }}>
                  {speaking ? 'Bol raha hai...' : listening ? 'Sun raha hai...' : 'CRM Assistant'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setAutoListen(v => !v)}
                className={`p-2 rounded-lg transition-colors ${autoListen ? 'bg-emerald-500/20' : 'hover:bg-white/10'}`}
                title={autoListen ? 'Auto-listen ON' : 'Auto-listen OFF'}
                data-testid="auto-listen-btn"
              >
                <Mic size={14} className={autoListen ? 'text-emerald-400' : 'text-slate-400'} />
              </button>
              <button
                onClick={() => { const next = !voiceEnabled; setVoiceEnabled(next); if (!next) stopAudio(); }}
                className="p-2 rounded-lg hover:bg-white/10 transition-colors"
                title={voiceEnabled ? 'Mute voice' : 'Enable voice'}
                data-testid="voice-toggle-btn"
              >
                {voiceEnabled ? <Volume2 size={14} style={{ color: BRAND.gold }} /> : <VolumeX size={14} className="text-slate-400" />}
              </button>
              <button onClick={clearChat} className="p-2 rounded-lg hover:bg-white/10 transition-colors" title="Clear chat">
                <Trash2 size={14} className="text-slate-400" />
              </button>
              <button onClick={() => { stopAudio(); setOpen(false); }} className="p-2 rounded-lg hover:bg-white/10 transition-colors" data-testid="assistant-close-btn">
                <X size={16} className="text-white" />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div ref={chatBodyRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50/50" style={{ minHeight: 0 }}>
            {messages.length === 0 && !loading && (
              <div className="text-center py-8">
                <div className="w-16 h-16 rounded-full mx-auto mb-3 flex items-center justify-center" style={{ background: `${BRAND.gold}15` }}>
                  <Sparkles size={28} style={{ color: BRAND.gold }} />
                </div>
                <p className="font-semibold text-sm" style={{ color: BRAND.navy }}>Namaste! Main Ganesa AI hoon</p>
                <p className="text-xs text-slate-400 mt-1">Mujhse CRM ke baare mein kuch bhi poochein</p>
                <div className="mt-3 flex items-center justify-center gap-2 text-xs text-slate-400">
                  <Mic size={12} className="text-emerald-500" />
                  <span>Auto-listen ON - Bas bolo, main sunuunga!</span>
                </div>
                <div className="mt-4 space-y-1.5">
                  {['Aaj ka revenue kitna hai?', 'Pending payments wale customers batao', 'Overdue follow-ups batao'].map((q, i) => (
                    <button key={i} onClick={() => sendMessage(q)} className="block w-full text-left px-3 py-2 rounded-lg border border-slate-200 text-xs text-slate-600 hover:bg-white hover:border-slate-300 transition-colors" data-testid={`quick-q-${i}`}>
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((msg) => (
              <div key={msg.id} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div
                  className={`max-w-[85%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed ${
                    msg.role === 'user'
                      ? 'rounded-br-md text-white'
                      : 'rounded-bl-md bg-white border border-slate-100 text-slate-700 shadow-sm'
                  }`}
                  style={msg.role === 'user' ? { background: BRAND.navy } : {}}
                >
                  <p className="whitespace-pre-wrap">{msg.content}</p>
                  {msg.role === 'assistant' && voiceEnabled && (
                    <button
                      onClick={() => playVoice(msg.content, false)}
                      className="mt-1.5 flex items-center gap-1 text-xs opacity-50 hover:opacity-100 transition-opacity"
                      style={{ color: BRAND.gold }}
                      data-testid={`replay-voice-${msg.id}`}
                    >
                      <Volume2 size={12} /> Sunein
                    </button>
                  )}
                </div>
              </div>
            ))}

            {loading && (
              <div className="flex justify-start">
                <div className="bg-white border border-slate-100 rounded-2xl rounded-bl-md px-4 py-3 shadow-sm">
                  <div className="flex items-center gap-1.5">
                    <div className="w-2 h-2 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: '0ms' }}></div>
                    <div className="w-2 h-2 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: '150ms' }}></div>
                    <div className="w-2 h-2 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: '300ms' }}></div>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input Area */}
          <div className="shrink-0 border-t border-slate-100 bg-white px-3 py-2.5">
            {speaking && (
              <div className="flex items-center justify-between mb-2 px-2 py-1.5 rounded-lg bg-amber-50 border border-amber-200">
                <span className="text-xs text-amber-700 font-medium flex items-center gap-1.5">
                  <Volume2 size={12} className="animate-pulse" /> Bol raha hai...
                </span>
                <button onClick={stopAudio} className="text-xs text-amber-600 hover:text-amber-800 font-medium" data-testid="stop-audio-btn">
                  Stop
                </button>
              </div>
            )}
            <div className="flex items-center gap-2">
              <button
                onClick={toggleVoice}
                className={`p-2.5 rounded-xl transition-all shrink-0 ${
                  listening
                    ? 'bg-red-500 text-white animate-pulse shadow-lg shadow-red-200'
                    : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                }`}
                data-testid="voice-btn"
                title={listening ? 'Stop recording' : 'Start voice input'}
              >
                {listening ? <MicOff size={16} /> : <Mic size={16} />}
              </button>
              <input
                ref={inputRef}
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={listening ? 'Sun raha hoon...' : 'Type ya bol ke poochein...'}
                className={`flex-1 px-3 py-2.5 rounded-xl border text-sm focus:outline-none focus:ring-2 focus:ring-opacity-50 ${
                  listening ? 'border-red-300 bg-red-50 focus:ring-red-400' : 'border-slate-200 focus:ring-blue-400'
                }`}
                disabled={loading}
                data-testid="assistant-input"
              />
              <button
                onClick={() => sendMessage()}
                disabled={!input.trim() || loading}
                className="p-2.5 rounded-xl transition-all shrink-0 text-white disabled:opacity-40"
                style={{ background: BRAND.navy }}
                data-testid="assistant-send-btn"
              >
                <Send size={16} />
              </button>
            </div>
            {listening && (
              <p className="text-xs text-red-500 mt-1 text-center font-medium animate-pulse">
                Mic ON - Boliye...
              </p>
            )}
          </div>
        </div>
      )}
    </>
  );
}
